import { useMemo } from 'react'
import { useLiveQuery as useLiveQueryBase } from 'next-sanity/preview'

/**
 * When not in draft mode we still must call the preview-kit hook (rules of hooks),
 * but we pass `null` so its mount JSON clone is trivial. Real server data is returned
 * from the parallel useMemo path — never the live hook result when draftMode is false.
 */
function skipLivePlaceholder<QueryResult>(): QueryResult {
  return null as unknown as QueryResult
}

/**
 * Wraps next-sanity's useLiveQuery. When not in draft/preview mode, returns
 * server props directly and avoids JSON.stringify(initialData) on huge Sanity
 * trees (that clone can throw RangeError before isEqual runs).
 */
export function useLiveQuery<QueryResult>(
  draftMode: boolean,
  initialData: QueryResult,
  query: string,
  queryParams?: Record<string, unknown>,
  options?: { isEqual?: (a: QueryResult, b: QueryResult) => boolean },
) {
  // Only enable live queries when explicitly requested.
  // This prevents production stacks from blowing up on large Sanity payloads
  // in Netlify/SSR environments while still allowing live mode locally.
  const enableLive =
    process.env.NEXT_PUBLIC_ENABLE_SANITY_LIVE === 'true' &&
    process.env.NODE_ENV !== 'test'

  const shouldUseLive = draftMode && enableLive

  // Debug: log which queries are considered "live" (only when enabled).
  if (
    process.env.NEXT_PUBLIC_DEBUG_SANITY_LIVEQUERY === 'true' &&
    typeof window !== 'undefined'
  ) {
    // Keep logs short; `query` is enough to identify the failing hook.
    // eslint-disable-next-line no-console
    console.log('[sanity-live-query]', {
      shouldUseLive,
      draftMode,
      query: String(query).slice(0, 80),
    })
  }

  const live = useLiveQueryBase(
    shouldUseLive ? initialData : skipLivePlaceholder<QueryResult>(),
    query,
    queryParams,
    shouldUseLive
      ? options
      : {
          ...options,
          isEqual: (a: unknown, b: unknown) => a === b,
        },
  )

  const staticResult = useMemo((): [QueryResult, boolean] => [initialData, false], [initialData])

  return shouldUseLive ? live : staticResult
}
